import {
  FieldErrors,
  FieldPath,
  FieldValues,
  get,
  useFormState,
} from 'react-hook-form';
import React, { RefObject, useMemo } from 'react';
import styled from 'styled-components';
import { colorErrorBg, colorWarningBg } from '@atlas-frontend/design-tokens';
import { ValidationMessagesMap } from '../../utils/ValidationMessagesMap';

export interface FormSegmentLabelFaultColorBgProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData>
> {
  name: Name | Name[];
  labelElementRef: RefObject<HTMLElement | null>;
}

export const FormSegmentLabelFaultColorBg = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData>
>(
  props: FormSegmentLabelFaultColorBgProps<FormData, Name>
) => {
  const { name } = props;
  const faults = useHasFaults(name);

  if (faults === 'none') {
    return null;
  }

  return (
    <FaultBg $color={faults === 'errors' ? colorErrorBg : colorWarningBg} />
  );
};

const useHasFaults = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData>
>(
  name: Name | Name[]
): 'errors' | 'warnings' | 'none' => {
  const { errors } = useFormState<FormData>({
    name, // does not seem to have any effect but ok, maybe in future versions it will have
  });
  const { totalErrors, totalWarnings } = useMemo(() => {
    let fieldErrors: FieldErrors;
    if (Array.isArray(name)) {
      fieldErrors = name.reduce((acc: FieldErrors, field: Name) => {
        const currentErrors = get(errors, field) || {};
        return {
          ...acc,
          ...currentErrors,
        };
      }, {});
    } else {
      fieldErrors = get(errors, name) || {};
    }
    return ValidationMessagesMap.fromFieldErrors(fieldErrors);
  }, [errors, name]);
  return totalErrors ? 'errors' : totalWarnings ? 'warnings' : 'none';
};

const FaultBg = styled.div<{ $color: string }>`
  position: absolute;
  z-index: -1;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  background-color: ${({ $color }) => $color};
  border-radius: inherit;
`;
